import { Box, CssBaseline, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import PageStateHandler from "./components/PageStateHandler";
import ProtectedRoute from "./components/ProtectedRoute";
import Sidebar from "./components/Sidebar";
import QaEnvironmentBanner from "./components/common/QaEnvironmentBanner";
import { ThemeSwitcherProvider } from "./contexts/ThemeSwitcherContext";
import { ToastProvider } from "./contexts/ToastContext";
import CitiesPage from "./pages/CitiesPage";
import CreateTruckGroupPage from "./pages/CreateTruckGroupPage";
import DataImporterPage from "./pages/DataImporterPage";
import DefaultTruckGroupsPage from "./pages/DefaultTruckGroupsPage";
import LoginPage from "./pages/LoginPage";
import {
  LogislyDriversPage,
  LogislyLocationsPage,
  LogislyTrucksPage,
} from "./pages/LogislyCatalogPages";
import LogislyLocationGroupsPage from "./pages/LogislyLocationGroupsPage";
import LogislyOrderTakerConfigsV2OpenPage from "./pages/LogislyOrderTakerConfigsV2OpenPage";
import LogislySpxOnCallPage from "./pages/LogislySpxOnCallPage";
import LogislySpxOnCallStatisticsPage from "./pages/LogislySpxOnCallStatisticsPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import RetiredTrucksPage from "./pages/RetiredTrucksPage";
import TruckGroupsPage from "./pages/TruckGroupsPage";
import TrucksPage from "./pages/TrucksPage";
import TutorialPage from "./pages/TutorialPage";
import UsersPage from "./pages/UsersPage";
import VendorsPage from "./pages/VendorsPage";
import type { AppDispatch, RootState } from "./store";
import { fetchCities } from "./store/features/citySlice";
import { fetchUserData } from "./store/features/userSlice";
import { QA_BANNER_HEIGHT_PX, isQaBannerEnabled } from "./utils/environment";
import "./App.css";
import "./translations/i18n"; // Import i18n configuration

// Placeholder components - you can replace these with your actual components
const Dashboard = () => <div>Dashboard</div>;
const Profile = () => <div>Profile</div>;
const Settings = () => <div>Settings</div>;
function AppContent() {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.user);
  const { isTempPassword } = user || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";
  const isResetPasswordPage = location.pathname === "/reset-password";
  const showQaBanner = isQaBannerEnabled();

  useEffect(() => {
    // Fetch user data if token exists
    dispatch(fetchUserData());
    // Fetch cities
    dispatch(fetchCities());
  }, [dispatch]);

  useEffect(() => {
    if (!isMobile) {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        m: 0,
        p: 0,
        pt: showQaBanner ? `${QA_BANNER_HEIGHT_PX}px` : 0,
      }}
    >
      <QaEnvironmentBanner visible={showQaBanner} />
      <PageStateHandler />
      {isAuthenticated && !isLoginPage && (!isResetPasswordPage || !isTempPassword) && (
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          qaBannerVisible={showQaBanner}
        />
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: { xs: 1, sm: 3, md: 3 },
          pt: { xs: 8, sm: 3 },
          width: "100%",
          py: 3,
        }}
      >
        <Routes>
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/trucks" replace /> : <LoginPage />}
          />
          {/* <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/trucks"
            element={
              <ProtectedRoute>
                <TrucksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trucksByType"
            element={
              <ProtectedRoute>
                <DefaultTruckGroupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/retiredTrucks"
            element={
              <ProtectedRoute>
                <RetiredTrucksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trucks/groups"
            element={
              <ProtectedRoute>
                <TruckGroupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trucks/groups/create"
            element={
              <ProtectedRoute>
                <CreateTruckGroupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trucks/groups/:groupId"
            element={
              <ProtectedRoute>
                <TruckGroupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trucks/groups/:groupId/edit"
            element={
              <ProtectedRoute>
                <TruckGroupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/locations"
            element={
              <ProtectedRoute>
                <CitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendors"
            element={
              <ProtectedRoute>
                <VendorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tutorial"
            element={
              <ProtectedRoute>
                <TutorialPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <ProtectedRoute allowTempPassword={true}>
                <ResetPasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dev-tools/data-importer"
            element={
              <ProtectedRoute>
                <DataImporterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logisly-spx-oncall"
            element={
              <ProtectedRoute>
                <LogislySpxOnCallPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logisly-spx-oncall/statistics"
            element={
              <ProtectedRoute>
                <LogislySpxOnCallStatisticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logisly-spx-oncall/drivers"
            element={
              <ProtectedRoute>
                <LogislyDriversPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logisly-spx-oncall/trucks"
            element={
              <ProtectedRoute>
                <LogislyTrucksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logisly-spx-oncall/locations"
            element={
              <ProtectedRoute>
                <LogislyLocationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logisly-spx-oncall/location-groups"
            element={
              <ProtectedRoute>
                <LogislyLocationGroupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logisly-spx-oncall/order-taker-configs-v2"
            element={
              <ProtectedRoute>
                <LogislyOrderTakerConfigsV2OpenPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logisly-spx-oncall/order-taker-configs-v2/open"
            element={<Navigate to="/logisly-spx-oncall/order-taker-configs-v2" replace />}
          />
          <Route
            path="/logisly-spx-oncall/order-taker-configs-v2/history"
            element={<Navigate to="/logisly-spx-oncall/order-taker-configs-v2" replace />}
          />
          <Route path="/" element={<Navigate to="/trucks" replace />} />
          <Route path="*" element={<Navigate to="/trucks" replace />} />
        </Routes>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <Router>
      <ThemeSwitcherProvider>
        <CssBaseline />
        <ErrorBoundary>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </ErrorBoundary>
      </ThemeSwitcherProvider>
    </Router>
  );
}

export default App;
